import React, { useState } from "react";
import { FaQrcode, FaExternalLinkAlt } from "react-icons/fa";
import ShareButton from "./ShareButton";
import InvitationBusinessCard from "./InvitationBusinessCard";
import "./ActivationLinkActions.css";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["activationLinkActions"]?.[key] || fallback;

// Single shared "Share / Copy / QR code" action group for a
// User.ativator[].token activation link (/user_Shoosing/:token) -- used
// identically by CardUser's activation modal and addAdminFamily's
// post-success card. Never a second/forked implementation of this trio
// (mission section 4/15). The Share+Copy icons are the same ShareButton
// ShopUser's header and ProductPreview already use.
//
// `shareMessage` (optional): the project's *existing*, already-prepared
// invitation message (mission section 3) -- never replaced, only ever
// extended with the link appended once, as its own paragraph. Falls back to
// a short "fullName — roleName" line when no existing message is supplied.
const ActivationLinkActions = ({
  infoSource,
  activationUrl,
  user,
  fullName,
  roleName,
  expirationDate,
  shareMessage,
  expired = false,
  onSuccess,
  onError,
}) => {
  __setElakayInfoSource(infoSource);
  const [showInvitationCard, setShowInvitationCard] = useState(false);

  const shareTitle = roleName || __elakayText("shareTitle", "Elakay invitation");
  const baseMessage = shareMessage || (fullName && roleName ? `${fullName} — ${roleName}` : (fullName || roleName || shareTitle));
  // The link is appended exactly once, as its own paragraph, to whatever
  // message already exists -- never sent alone, never duplicated.
  const completeMessage = activationUrl ? `${baseMessage}\n\n${activationUrl}` : baseMessage;
  const actionsDisabled = !activationUrl || expired;

  return (
    <div className="activation-link-actions">
      {!activationUrl && (
        <p className="activation-link-actions-warning" role="alert">
          {__elakayText("noLink", "No valid activation link is available.")}
        </p>
      )}
      {activationUrl && expired && (
        <p className="activation-link-actions-warning" role="alert">
          {__elakayText("expiredMessage", "This activation link has expired.")}
        </p>
      )}

      <div className="activation-link-actions-row">
        <ShareButton
          infoSource={infoSource}
          url={activationUrl}
          title={shareTitle}
          text={completeMessage}
          combineTextAndUrl
          showCopyButton
          disabled={actionsDisabled}
          onShared={() => onSuccess?.(__elakayText("shared", "Invitation shared."))}
          onCopied={() => onSuccess?.(__elakayText("messageCopied", "The message and the activation link have been copied."))}
          onError={() => onError?.(__elakayText("shareError", "Unable to share this activation link."))}
        />

        <button
          type="button"
          className="activation-link-actions-qr-btn"
          onClick={() => setShowInvitationCard((prev) => !prev)}
          disabled={actionsDisabled}
          aria-label={showInvitationCard ? __elakayText("hideQr", "Hide QR code") : __elakayText("showQr", "Show QR code")}
          title={showInvitationCard ? __elakayText("hideQr", "Hide QR code") : __elakayText("showQr", "Show QR code")}
        >
          <FaQrcode />
          <span className="activation-link-actions-qr-label">{__elakayText("qrLabel", "QR Code")}</span>
        </button>


<a
  href={activationUrl}
  target="_blank"
  rel="noopener noreferrer"
  className="activation-link-actions-qr-btn"
  aria-label={__elakayText(
    "openActivationLink",
    "Open activation link"
  )}
  title={__elakayText(
    "openActivationLink",
    "Open activation link"
  )}
>
  <FaExternalLinkAlt />
  <span className="activation-link-actions-qr-label">
    {__elakayText("openLink", "Open Link")}
  </span>
</a>
      </div>

      <InvitationBusinessCard
        show={showInvitationCard && !expired}
        infoSource={infoSource}
        userInfo={user}
        fullName={fullName}
        roleName={roleName}
        activationUrl={activationUrl}
        expirationDate={expirationDate}
      />
    </div>
  );
};

export default ActivationLinkActions;
